// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — markVariants.js   ·   THE THREE SHELVES, as data
// ────────────────────────────────────────────────────────────────────
// THE MARK is the player (§3.7b). This file is what the Mark is PRINTED
// ON — the plate, the foil, the material. Nothing here touches identity,
// standing, deeds or currency. A variant is a skin and only a skin.
//
// ── THE LAW THIS FILE ENFORCES (IE_CANON_PRIME §2.15, 2026-08-25) ───
// Supply decides WHICH shelf a thing sits on. The shelf decides WHAT IT
// LOOKS LIKE. Three shelves, everywhere in the Empire, forever:
//
//   FABLE      Holographic but plain. ONE flat colour typically;
//              THREE is the ceiling. Beautiful, never elaborate.
//              TEST: count the colours.
//
//   LEGENDARY  The absolute best holographic you will ever see —
//              and it DOES NOT MOVE. Every trick a still image allows.
//              TEST: freeze it. If freezing takes nothing away, it's
//              Legendary.
//
//   MYTHICAL   "Lord." The ASCENDED form of its OWN Legendary
//              predecessor. Exactly the same design, plus animation,
//              plus the flare that only animation makes possible.
//              TEST: put it beside its Legendary. Not recognisably
//              that same piece ascended? Then it isn't Mythical.
//
// ── WHY THE ASCENSION IS A FOREIGN KEY AND NOT A COMMENT ────────────
// Law consequence #1 says a Mythical is never a NEW design. A comment
// saying so decays in a month. So a mythical entry does not carry a
// recipe at all — it carries `ascendedFrom`, and MarkFoil RESOLVES the
// recipe out of that Legendary and switches animation on. A Mythical
// that drifts from its Legendary is therefore not a bug we have to
// catch: it is UNREPRESENTABLE. (ie-3d-conversion: delete the degree
// of freedom, not the bug.)
//
// `auditShelves()` closes the rest — colour ceilings, the no-motion
// rule, the dangling-ascension rule, and Mythical-vs-supply. It returns
// [] when the catalogue is lawful. Run it after adding ANY entry.
//
// ── SUPPLY (IE_CANON_PRIME §3.2 E) ──────────────────────────────────
// A shelf is a LOOK. Supply is separate and still binding — but read
// the ruling below before writing a limit into anything.
//
// ⚠ caP'n, 2026-08-25, overruling a limit V'ael invented and nobody
// asked for: **"Mythicals are rare but that doesn't mean it should
// stop you from getting others."**
//
// ONE PER ACCOUNT MEANS ONE OF *THAT PLATE*, NOT ONE MYTHICAL EVER.
// Rarity is a property of the ITEM's supply. It is not a quota on the
// player. A shop that lets you buy a Lord and then refuses to sell you
// the next one is not modelling scarcity — it is punishing you for
// having spent, and it makes the second and third Lord unsellable
// content nobody will ever see.
//
// So there is no cross-item Mythical block here, and there must not be
// one. What remains true, and is enough: you cannot buy the same plate
// twice (the ownership lane refuses it), and a Mythical still costs
// Crests rather than a title's own coin.
// ════════════════════════════════════════════════════════════════════

export const MARK_VARIANTS_VERSION = "1.0";

// ── THE SHELVES ─────────────────────────────────────────────────────
// One place. A fourth shelf is a bug — say so out loud rather than
// adding a row here. (THE THREE-LEVEL LAW, §2.18 Law 23.)
export const SHELVES = {
  fable: {
    id: "fable",
    label: "FABLE",
    glyph: "◇",
    tint: "#5fa79f",
    tintHi: "#9fd8d0",
    rule: "Holographic, and plain about it. One flat colour, three at the ceiling.",
    test: "Count the colours.",
    maxColors: 3,
    animated: false,
    order: 0,
  },
  legendary: {
    id: "legendary",
    label: "LEGENDARY",
    glyph: "♥︎",
    tint: "#d4a03c",
    tintHi: "#f6dfa0",
    rule: "The best holographic there is — and it does not move. Every trick a still image allows.",
    test: "Freeze it. If freezing takes nothing away, it is Legendary.",
    maxColors: Infinity,
    animated: false,
    order: 1,
  },
  mythical: {
    id: "mythical",
    label: "MYTHICAL",
    glyph: "◆",
    tint: "#7c3aed",
    tintHi: "#c4b5fd",
    rule: "Lord. The ascended form of its own Legendary — same design, now moving, and doing what only motion can.",
    test: "Put it beside its Legendary. It must read as that same piece, ascended.",
    maxColors: Infinity,
    animated: true,
    order: 2,
  },
};

export const SHELF_ORDER = ["fable", "legendary", "mythical"];

export const shelfOf = (v) => SHELVES[v?.shelf] || SHELVES.fable;

// ── THE CATALOGUE ───────────────────────────────────────────────────
// `plate`   — the slab the Mark is printed on (always present).
// `colors`  — FABLE ONLY. The flat foil colours. Length is the law.
// `recipe`  — LEGENDARY ONLY. The still-holo build. A mythical must NOT
//             carry one; it inherits its Legendary's, by construction.
// `ascendedFrom` — MYTHICAL ONLY. The legendary id it is the Lord of.
// `flare`   — MYTHICAL ONLY. What animation buys, on top. Never a
//             different design — only motion and what motion allows.
//
// Prices are STICKERS read by the shop from HERE and never from the
// caller (§ storefront rule 1). The till being off does not change them.

export const MARK_VARIANTS = {
  // ══ FABLE ══════════════════════════════════════════════════════════
  classic: {
    id: "classic",
    name: "The Classic",
    shelf: "fable",
    blurb:
      "The Mark as it was struck. Gold leaf on obsidian, one colour, no argument. " +
      "Every citizen owns it and nobody is embarrassed to wear it.",
    colors: ["#d4a03c"],
    plate: { slab: "#12101a", deep: "#0b0910", edge: "#241f31" },
    priceDC: 0,
    default: true,
    seal: "The first plate. Nothing has ever replaced it.",
  },
  salt_verdigris: {
    id: "salt_verdigris",
    name: "Salt & Verdigris",
    shelf: "fable",
    blurb:
      "Pier brass left out in the spray for a season. Two colours: the green " +
      "that grows on it and the bone it grows over.",
    colors: ["#5fa79f", "#ece6da"],
    plate: { slab: "#0f1518", deep: "#070c0e", edge: "#1d3033" },
    priceDC: 2,
    seal: "W'ily swears he polishes it. W'ily does not polish it.",
  },
  bloodline: {
    id: "bloodline",
    name: "Bloodline",
    shelf: "fable",
    blurb:
      "Struck for people who would rather be read than liked. Two colours, " +
      "one of them unmistakable.",
    colors: ["#c01427", "#59536a"],
    plate: { slab: "#150e11", deep: "#0a0507", edge: "#341c22" },
    priceDC: 2,
    seal: "The Empire has always been legible in red.",
  },
  violet_tide: {
    id: "violet_tide",
    name: "Violet Tide",
    shelf: "fable",
    blurb:
      "The colour the void goes after dark, caught flat on a plate. Three " +
      "colours — the ceiling, and it knows it.",
    colors: ["#7c3aed", "#22d3ee", "#06060a"],
    plate: { slab: "#100c1c", deep: "#07050f", edge: "#281f44" },
    priceDC: 3,
    seal: "Held up to the lamps it looks like the water under the planks.",
  },
  boardwalk_bulb: {
    id: "boardwalk_bulb",
    name: "Boardwalk Bulb",
    shelf: "fable",
    blurb:
      "The exact amber of the string lights over the spine, plus the two " +
      "colours they throw. Pierre's own plate.",
    colors: ["#f0d060", "#f59e0b", "#ece6da"],
    plate: { slab: "#171208", deep: "#0c0904", edge: "#3a2c12" },
    priceDC: 3,
    seal: "Mystic Ally stocks more of these than anything else. Nobody minds.",
  },

  // ══ LEGENDARY ══════════════════════════════════════════════════════
  // Still. Elaborate. Every one its own recipe — never a recolor of a
  // sibling (THE HOLO LAW, §2.15). Freeze-test each before shipping.
  emperium_plate: {
    id: "emperium_plate",
    name: "Emperium Plate",
    shelf: "legendary",
    blurb:
      "Struck the way the domain is struck: a full spectrum laid under a " +
      "milled sunburst, then pressed flat and left alone. It does not move " +
      "because it does not need to.",
    plate: { slab: "#141019", deep: "#08060c", edge: "#3a2f18" },
    recipe: {
      geometry: "sunburst",        // conic spectrum, milled radial spokes
      spectrum: ["#f6dfa0", "#d4a03c", "#7a5c1f", "#ece6da", "#d4a03c", "#f6dfa0"],
      spokes: 36,
      grain: "fine",               // micro diagonal print-lines
      turbulence: { freq: 0.9, scale: 14, seed: 0x50fa },
      bloom: "#f6dfa0",
      rim: "#d4a03c",
      opacity: 0.62,
    },
    priceDC: 18,
    seal: "The Inner Emperium does not sign things. It stamps them.",
  },
  khao_glass: {
    id: "khao_glass",
    name: "Khao Glass",
    shelf: "legendary",
    blurb:
      "Fuel cooled too fast. A single frozen fracture across the plate with " +
      "the spectrum trapped inside the crack. Perfectly, permanently still.",
    plate: { slab: "#0d1116", deep: "#05080b", edge: "#1c2c3a" },
    recipe: {
      geometry: "fracture",        // seeded polyline shatter, static
      spectrum: ["#22d3ee", "#a5f3fc", "#0e7490", "#ece6da", "#22d3ee"],
      shards: 14,
      grain: "glass",
      turbulence: { freq: 1.6, scale: 9, seed: 0x1c3f },
      bloom: "#a5f3fc",
      rim: "#22d3ee",
      opacity: 0.58,
    },
    priceDC: 18,
    seal: "Khao is the patience tax. This is what it looks like paid in full.",
  },
  lyt_prism: {
    id: "lyt_prism",
    name: "Lyt Prism",
    shelf: "legendary",
    blurb:
      "Light entering a plate and refusing to leave the same colour. Bands " +
      "stacked to the millimetre. Nothing on it moves; everything on it bends.",
    plate: { slab: "#0f0d1a", deep: "#07060e", edge: "#2b2350" },
    recipe: {
      geometry: "prism",           // stacked refraction bands + caustic
      spectrum: ["#7c3aed", "#c4b5fd", "#22d3ee", "#f6dfa0", "#c01427", "#7c3aed"],
      bands: 9,
      grain: "fine",
      turbulence: { freq: 0.6, scale: 20, seed: 0x7c3a },
      bloom: "#c4b5fd",
      rim: "#7c3aed",
      opacity: 0.6,
    },
    priceDC: 18,
    seal: "Hold it at the wrong angle and it is briefly someone else's Mark.",
  },

  // ══ MYTHICAL ═══════════════════════════════════════════════════════
  // NO `recipe` KEY. On purpose. The recipe is resolved through
  // `ascendedFrom`, which is how "same design, ascended" stops being a
  // promise and becomes the only thing the code can express.
  emperium_ascendant: {
    id: "emperium_ascendant",
    name: "Emperium Ascendant",
    shelf: "mythical",
    ascendedFrom: "emperium_plate",
    blurb:
      "The same plate, woken up. The sunburst turns. The spectrum walks the " +
      "milling. Gold lifts off the surface and settles back down, and the rim " +
      "breathes like something with an opinion.",
    flare: {
      spin: 42,          // seconds per revolution of the sunburst
      sheen: 5.5,        // sweep period
      hueDrift: 9,
      motes: 14,
      rimPulse: 3.2,
      lift: true,        // the foil parallaxes off the slab
    },
    priceDC: 90,
    seal: "A Lord is not a bigger thing. It is the same thing, and it is awake.",
  },
  khao_unbound: {
    id: "khao_unbound",
    name: "Khao Unbound",
    shelf: "mythical",
    ascendedFrom: "khao_glass",
    blurb:
      "The fracture never finished. Shards drift a hair out of true and snap " +
      "back; the trapped spectrum runs the crack end to end; the plate rings " +
      "and re-seals itself, forever.",
    flare: {
      spin: 0,
      sheen: 3.4,
      hueDrift: 6,
      motes: 20,
      rimPulse: 2.1,
      shatter: 2.9,      // the drift-and-reseal cycle, mythical-only
      lift: true,
    },
    priceDC: 90,
    seal: "Cooling too fast was never the mistake. Stopping was.",
  },
  lyt_eternal: {
    id: "lyt_eternal",
    name: "Lyt Eternal",
    shelf: "mythical",
    ascendedFrom: "lyt_prism",
    blurb:
      "The bands stop being stacked and start being a current. Caustics crawl " +
      "the plate, the refraction runs the long way, and the Mark reads a little " +
      "differently every time you look down at it.",
    flare: {
      spin: 0,
      sheen: 6.5,
      hueDrift: 22,      // the big one — the whole plate walks the spectrum
      motes: 26,
      rimPulse: 4.4,
      caustic: 7.5,
      lift: true,
    },
    priceDC: 90,
    seal: "Light that got out, came back, and brought something with it.",
  },
};

export const DEFAULT_VARIANT = "classic";

export const variantById = (id) => MARK_VARIANTS[id] || MARK_VARIANTS[DEFAULT_VARIANT];

// The variant a Mark is currently WEARING. Self-authored cosmetic text,
// so it lives in the existing `profiles/{uid}/mark` node — no new node,
// no new rule. An unknown id self-heals to the Classic rather than
// rendering nothing: retiring a variant must never blank somebody's ID.
export const wornVariant = (mark) => variantById(mark?.variant);

export const variantsOnShelf = (shelf) =>
  Object.values(MARK_VARIANTS)
    .filter((v) => v.shelf === shelf)
    .sort((a, b) => (a.priceDC || 0) - (b.priceDC || 0) || a.name.localeCompare(b.name));

// ── ⭐ THE WHEEL — the order plates cycle in ─────────────────────────
// caP'n, 2026-08-25: "changing marks is a bit tedious — what if when
// you click 'The Mark' words on the mark it cycles between your
// available marks."
//
// So the ordering rule lives HERE, with the catalogue, and not at the
// click site: **the wheel climbs the shelves.** Classic first (everyone
// owns it and it is where you started), then Fable, then Legendary,
// then Mythical, each shelf alphabetical inside itself. Clicking
// through your plates therefore walks UP, which is the only order that
// tells you anything.
//
// A plate the player owns that is no longer in the catalogue is
// silently skipped rather than crashing the wheel — retiring a plate
// must never strand somebody on a page they cannot click off.
export function plateWheel(ownedIds = []) {
  const owned = new Set(ownedIds.filter((id) => MARK_VARIANTS[id]));
  owned.delete(DEFAULT_VARIANT);
  const rest = [...owned].sort((a, b) => {
    const A = MARK_VARIANTS[a], B = MARK_VARIANTS[b];
    return (SHELVES[A.shelf].order - SHELVES[B.shelf].order) || A.name.localeCompare(B.name);
  });
  return [DEFAULT_VARIANT, ...rest];
}

// The next plate on the wheel after `current`. Wraps, and tolerates a
// `current` that is not on the wheel at all (a plate that was retired,
// or one worn before it was owned) by starting from the top.
export function nextPlate(ownedIds, current) {
  const wheel = plateWheel(ownedIds);
  if (wheel.length < 2) return null;
  const i = wheel.indexOf(current);
  return wheel[(i < 0 ? 0 : i + 1) % wheel.length];
}

// ── THE ASCENSION RESOLVER ──────────────────────────────────────────
// The one function that makes law consequence #1 structural. Every
// renderer goes through it; none of them may read `recipe` directly.
export function resolveRecipe(variant) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  if (!v) return null;
  if (v.shelf === "fable") {
    return { kind: "fable", colors: (v.colors || []).slice(0, SHELVES.fable.maxColors), animated: false };
  }
  if (v.shelf === "legendary") {
    return { kind: "holo", ...v.recipe, animated: false, flare: null };
  }
  // MYTHICAL — the recipe is its Legendary's, full stop.
  const base = MARK_VARIANTS[v.ascendedFrom];
  if (!base?.recipe) return null;   // audit catches this; render falls back
  return { kind: "holo", ...base.recipe, animated: true, flare: v.flare || {}, ascendedFrom: base.id };
}

// ── THE AUDIT ───────────────────────────────────────────────────────
// Returns [] when the catalogue obeys THE THREE SHELVES. Anything else
// is a list of sentences you can paste to caP'n. Run it after adding an
// entry; the shop runs it on mount in development.
export function auditShelves(catalogue = MARK_VARIANTS) {
  const out = [];
  const entries = Object.entries(catalogue);

  for (const [id, v] of entries) {
    if (id !== v.id) out.push(`${id}: key and .id disagree (.id is "${v.id}").`);
    if (!SHELVES[v.shelf]) { out.push(`${id}: shelf "${v.shelf}" is not one of the three. A fourth shelf is a bug.`); continue; }

    if (v.shelf === "fable") {
      const n = (v.colors || []).length;
      if (n === 0) out.push(`${id}: FABLE with no colours. Fable is flat holo — it needs at least one.`);
      if (n > SHELVES.fable.maxColors) out.push(`${id}: FABLE with ${n} colours. The ceiling is 3. Count the colours.`);
      if (v.recipe) out.push(`${id}: FABLE carrying a Legendary recipe. Fable is plain by law.`);
      if (v.flare) out.push(`${id}: FABLE carrying flare. Only Mythical moves.`);
      if (v.ascendedFrom) out.push(`${id}: FABLE ascending something. Only Mythical ascends.`);
    }

    if (v.shelf === "legendary") {
      if (!v.recipe) out.push(`${id}: LEGENDARY with no recipe. There is nothing to freeze.`);
      if (v.flare) out.push(`${id}: LEGENDARY carrying flare. Legendary DOES NOT MOVE — that is the whole line.`);
      if (v.ascendedFrom) out.push(`${id}: LEGENDARY ascending something. Ascension belongs to Mythical.`);
    }

    if (v.shelf === "mythical") {
      if (v.recipe) out.push(`${id}: MYTHICAL carrying its own recipe. A Mythical is never a new design — delete it and let ascendedFrom resolve.`);
      if (!v.ascendedFrom) out.push(`${id}: MYTHICAL with no ascendedFrom. It is a Legendary somebody animated and mislabelled.`);
      else {
        const base = catalogue[v.ascendedFrom];
        if (!base) out.push(`${id}: ascends "${v.ascendedFrom}", which is not in the catalogue.`);
        else if (base.shelf !== "legendary") out.push(`${id}: ascends "${v.ascendedFrom}", which is ${base.shelf.toUpperCase()}, not Legendary. Ascension climbs exactly one rung.`);
        else if (!base.recipe) out.push(`${id}: ascends "${v.ascendedFrom}", which has no recipe to inherit.`);
      }
      if (!v.flare || !Object.keys(v.flare).length) out.push(`${id}: MYTHICAL with no flare. Mythical MUST move.`);
      // ⚠ NO cross-item quota check here, deliberately. See the SUPPLY
      // note at the top of this file: one-per-account means one of THAT
      // plate, never one Mythical ever. If a future audit rule wants to
      // cap how many Lords a player may hold, it is wrong.
    }
  }

  // Every Legendary should eventually have a Lord. Not an error — a debt,
  // and the catalogue should be able to state its own debts.
  for (const [id, v] of entries) {
    if (v.shelf !== "legendary") continue;
    const has = entries.some(([, m]) => m.shelf === "mythical" && m.ascendedFrom === id);
    if (!has) out.push(`NOTE ${id}: Legendary with no Mythical above it. Legal, but the shelf above is empty.`);
  }
  return out;
}
